import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { SubscriptionList } from '@vendasta/rx-utils';
import { MEDDICSection } from '@vendasta/sales-opportunities';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, firstValueFrom, merge, of } from 'rxjs';
import { debounceTime, delay, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { MeddicNotesService, SaveMEDDICNotesRequest } from '../../meddic-notes.service';

export const SALESPERSON_ID_TOKEN = 'SALESPERSON_ID_TOKEN';

export enum MEDDICNoteStatus {
  SAVED = 0,
  SAVING = 1,
  ERROR = 2,
}

export interface MEDDICNoteChanges {
  notes: string;
  section: MEDDICSection;
}

const DELAY_BEFORE_SAVING_NOTES = 750;

@Component({
  selector: 'sales-ui-meddic-notes-field',
  templateUrl: './meddic-notes-field.component.html',
  styleUrls: ['./meddic-notes-field.component.scss'],
})
export class MeddicNotesFieldComponent implements OnDestroy {
  readonly formControl = new UntypedFormControl('');
  readonly NOTES_STATUS_SAVED = MEDDICNoteStatus.SAVED;
  readonly NOTES_STATUS_SAVING = MEDDICNoteStatus.SAVING;
  readonly NOTES_STATUS_ERROR = MEDDICNoteStatus.ERROR;
  notesSaveStatus$: Observable<MEDDICNoteStatus>;
  private readonly accountGroupId$$ = new ReplaySubject<string>(1);
  private readonly accountGroupId$ = this.accountGroupId$$.pipe(filter((id) => !!id));
  private readonly opportunityId$$ = new ReplaySubject<string>(1);
  private readonly opportunityId$ = this.opportunityId$$.pipe(filter((id) => !!id));
  private readonly fallbackSalespersonId$$ = new BehaviorSubject<string>('');
  readonly fallbackSalespersonId$ = this.fallbackSalespersonId$$.pipe(filter((id) => !!id));
  private readonly section$$ = new ReplaySubject<MEDDICSection>(1);
  readonly section$ = this.section$$.asObservable();
  private readonly subscriptions = SubscriptionList.new();
  private readonly noteRecorder$$ = new ReplaySubject<MEDDICNoteChanges>(1);

  constructor(
    @Inject(SALESPERSON_ID_TOKEN) private readonly salesPersonId$: Observable<string>,
    private readonly notesService: MeddicNotesService,
  ) {
    this.disableNotesFields();
    this.constructNoteChangeObservables();
    this.constructNoteChangeSubscriptions();
  }

  @Input() set section(section: MEDDICSection) {
    this.section$$.next(section);
  }

  @Input() set accountGroupId(id: string) {
    this.accountGroupId$$.next(id);
  }

  @Input() set fallbackSalespersonId(id: string) {
    this.fallbackSalespersonId$$.next(id);
  }

  @Input() set opportunityId(id: string) {
    this.opportunityId$$.next(id);
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  private disableNotesFields(): void {
    this.formControl.disable({ emitEvent: false });
  }

  private constructNoteChangeObservables(): void {
    const noteSaveStatus$ = this.opportunityId$.pipe(
      switchMap((id) =>
        combineLatest([
          this.notesService.areNotesCurrentlySaving$(id),
          this.notesService.didNotesSaveSuccessfully$(id),
        ]),
      ),
      switchMap(([isSaving, savedSuccessfully]) => {
        if (!isSaving && !savedSuccessfully) {
          return of(MEDDICNoteStatus.ERROR);
        }
        return isSaving ? of(MEDDICNoteStatus.SAVING) : of(MEDDICNoteStatus.SAVED).pipe(delay(350));
      }),
    );

    const loadedSuccessfully$ = this.opportunityId$.pipe(
      switchMap((id) => this.notesService.didSectionsLoadSuccessfully(id)),
      map((success) => (success ? MEDDICNoteStatus.SAVED : MEDDICNoteStatus.ERROR)),
    );

    this.notesSaveStatus$ = merge(noteSaveStatus$, loadedSuccessfully$);
  }

  private constructNoteChangeSubscriptions(): void {
    this.subscriptions.add(this.noteRecorder$$.asObservable(), (noteChangeRequest) =>
      this.sendMeddicNoteUpdateRequest(noteChangeRequest),
    );

    const noteChanges$ = combineLatest([this.formControl.valueChanges, this.section$]).pipe(
      map(([notes, section]) => <MEDDICNoteChanges>{ notes: notes, section: section }),
      debounceTime(DELAY_BEFORE_SAVING_NOTES),
    );

    this.subscriptions.add(noteChanges$, (request) => this.noteRecorder$$.next(request));

    const sectionLoadStatus$ = this.opportunityId$.pipe(
      switchMap((id) =>
        combineLatest([
          this.notesService.didSectionsLoadSuccessfully(id),
          this.notesService.areSectionsCurrentlyLoading$(id),
        ]),
      ),
    );
    this.subscriptions.add(sectionLoadStatus$, ([isSuccess, isLoading]) => {
      if (isSuccess && !isLoading) {
        this.formControl.enable({ emitEvent: false });
      } else {
        this.formControl.disable({ emitEvent: false });
      }
    });

    const thisSectionsNotes$ = this.opportunityId$.pipe(
      switchMap((id) => this.notesService.getMeddicSections(id)),
      withLatestFrom(this.section$),
      map(([sections, thisSection]) => sections.find((section) => section.sectionName === thisSection)),
    );

    this.subscriptions.add(thisSectionsNotes$, (section) => {
      if (section) this.formControl.setValue(section.notes, { emitEvent: false });
    });
  }

  private async sendMeddicNoteUpdateRequest(changes: MEDDICNoteChanges): Promise<void> {
    const agid = await firstValueFrom(this.accountGroupId$);
    const oppId = await firstValueFrom(this.opportunityId$);
    const salesPersonId = await firstValueFrom(this.salesPersonId$);
    const fallbackSalespersonId = await firstValueFrom(this.fallbackSalespersonId$);

    const req: SaveMEDDICNotesRequest = {
      accountGroupId: agid,
      opportunityId: oppId,
      editSalesPersonId: salesPersonId || fallbackSalespersonId,
      section: changes.section,
      notes: changes.notes,
    };
    this.notesService.saveMEDDICNotes(req);
  }
}
